import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../utils/axiosInstance";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import * as XLSX from "xlsx";

const EnhanceAndExpand = () => {
    const email = localStorage.getItem("email");

    const [uploadedFile, setUploadedFile] = useState(null);
    const [parsedData, setParsedData] = useState([]);
    const [fileType, setFileType] = useState("Domain List");
    const [operation, setOperation] = useState("Enhance Only");
    const [showOptions, setShowOptions] = useState(false);
    const [emailResults, setEmailResults] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // single source of truth for allowed operations per fileType
    const allowedOps = useMemo(
        () => ({
            "Domain List": ["Enhance Only", "Enhance & Upload"],
            "Email List": ["Enhance Only", "Enhance & Upload"],
            "LinkedIn URL": ["Enhance Only", "Expand & Enhance"],
        }),
        []
    );

    // sanitise operation whenever fileType or operation changes
    useEffect(() => {
        if (!allowedOps[fileType].includes(operation)) {
            setOperation(allowedOps[fileType][0]);
        }
    }, [fileType, operation, allowedOps]);

    // derive endpoint and instructions from a single mapping
    const { endpoint, instructions } = useMemo(() => {
        const map = {
            "Domain List": {
                "Enhance Only": {
                    endpoint: "/domain_file/domain/enhance/upload",
                    instructions: 'File must have a column named "Domain" (Enhance Only).',
                },
                "Expand & Enhance": {
                    endpoint: "/domain_file/domain/expand/upload",
                    instructions: 'File must have a column named "Domain" (Expand & Enhance).',
                },
                "Enhance & Upload": {
                    endpoint: "/domain_file/domain/uploadcrm/upload",
                    instructions:
                        'File must have a column named "Domain", other columns optional (Enhance & Upload).',
                },
            },
            "Email List": {
                "Enhance Only": {
                    endpoint: "/email_file/email/enhance/upload",
                    instructions:
                        'File must have a column named "Email", Optional: "Name", "Phone" and "Position" (Enhance Only).',
                },
                "Expand & Enhance": {
                    endpoint: "/email_file/email/expand/upload",
                    instructions: 'File must have a column named "Email" (Expand & Enhance).',
                },
                "Enhance & Upload": {
                    endpoint: "/email_file/email/uploadcrm/upload",
                    instructions:
                        'File must have a column named "Email", other columns optional (Enhance & Upload).',
                },
            },
            "LinkedIn URL": {
                "Enhance Only": {
                    endpoint: "/linkedin_file/linkedin/enhance/upload",
                    instructions: 'File must have a column named "Profile URL" (Enhance Only).',
                },
                "Expand & Enhance": {
                    endpoint: "/linkedin_file/linkedin/expand/upload",
                    instructions: 'File must have a column named "Profile URL" (Expand & Enhance).',
                },
            },
        };

        const cfg = map[fileType]?.[operation] ?? map[fileType]?.[allowedOps[fileType][0]];

        return {
            endpoint: cfg?.endpoint ?? "",
            instructions: cfg?.instructions ?? "",
        };
    }, [fileType, operation, allowedOps]);

    // reset/compute emailResults deterministically
    useEffect(() => {
        if (operation === "Expand & Enhance") {
            setEmailResults(fileType === "LinkedIn URL");
            return;
        }
        if (operation === "Enhance & Upload") {
            setEmailResults(true);
            return;
        }
        setEmailResults(fileType === "LinkedIn URL"); // Enhance Only
    }, [fileType, operation]);

    const handleFileTypeChange = (e) => setFileType(e.target.value);
    const handleOperationChange = (e) => setOperation(e.target.value);

    const handleDrop = (acceptedFiles) => {
        const valid = acceptedFiles.filter((f) =>
            ["text/csv", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"].includes(
                f.type
            )
        );
        if (valid.length !== acceptedFiles.length) {
            toast.error("Only CSV or Excel files are allowed.");
        }
        if (valid.length > 1) {
            return toast.error("Only one file is allowed.");
        }

        const file = valid[0];
        setUploadedFile(file);
        setShowOptions(true);

        const reader = new FileReader();
        reader.onload = () => {
            if (file.type === "text/csv") {
                Papa.parse(reader.result, {
                    header: true,
                    complete: (results) => setParsedData(results.data),
                    error: (err) => {
                        console.error("CSV parse error:", err);
                        toast.error("Error parsing CSV file.");
                    },
                });
            } else {
                try {
                    const wb = XLSX.read(reader.result, { type: "binary" });
                    const sheet = wb.Sheets[wb.SheetNames[0]];
                    setParsedData(XLSX.utils.sheet_to_json(sheet));
                } catch (err) {
                    console.error("Excel parse error:", err);
                    toast.error("Error parsing Excel file.");
                }
            }
        };

        if (file.type === "text/csv") reader.readAsText(file);
        else reader.readAsBinaryString(file);
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: handleDrop,
        accept: {
            "text/csv": [".csv"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
        },
        maxFiles: 1,
    });

    const handleSubmit = async () => {
        if (!uploadedFile) {
            return toast.error("Please upload a file before submitting.");
        }

        if (emailResults) {
            const rows = parsedData.length;

            const rowLimits = {
                "Domain List": {
                    "Enhance Only": 10000,
                    "Expand & Enhance": 10000,
                    "Enhance & Upload": 10000,
                },
                "LinkedIn URL": {
                    "Enhance Only": 200,
                    "Expand & Enhance": 30,
                },
                "Email List": {
                    "Enhance Only": 10000,
                    "Expand & Enhance": 10000,
                    "Enhance & Upload": 10000,
                },
            };

            const limit = rowLimits[fileType]?.[operation];

            if (limit && rows > limit) {
                return toast.warning(
                    `Row limit exceeded: The maximum is ${limit} rows. Uncheck "Email me results" or reduce rows (separate into files).`
                );
            }
        }

        if (!endpoint) {
            console.error("No endpoint resolved for", { fileType, operation });
            return toast.error("No endpoint resolved for current selection.");
        }

        const formData = new FormData();
        formData.append("file", uploadedFile);
        formData.append("to_address", email);
        formData.append("emailMeResults", emailResults ? "true" : "false");

        setIsSubmitting(true);
        try {
            console.debug("POST", endpoint, { fileType, operation });

            const response = await axiosInstance.post(endpoint, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (response.status === 200) {
                toast.success(response.data.message || "File uploaded successfully!");
                setUploadedFile(null);
                setParsedData([]);
                setShowOptions(false);
                setEmailResults(false);
            } else {
                throw new Error(response.data.message || "Upload failed.");
            }
        } catch (err) {
            console.error("Upload error:", err);
            const msg = err.response?.data?.message || err.message;
            toast.error("Error uploading file: " + msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const downloadExample = (columns, data, filename, sheetName) => {
        const ws = XLSX.utils.json_to_sheet(data, { header: columns });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        XLSX.writeFile(wb, filename);
    };

    const handleDownloadDomainExample = () =>
        downloadExample(["Domain"], [{ Domain: "https://ciilock.com/" }], "domain_example_input.xlsx", "Domain Example");

    const handleDownloadLinkedInExample = () =>
        downloadExample(
            ["Profile URL"],
            [{ "Profile URL": "https://www.linkedin.com/in/ciilockengineering/" }],
            "linkedin_example_input.xlsx",
            "LinkedIn Example"
        );

    const handleDownloadEmailExample = () =>
        downloadExample(["Email"], [{ Email: "charlie@ciilock.com" }], "email_example_input.xlsx", "Email Example");

    const handleDownloadEmailEnhanceExample = () =>
        downloadExample(
            ["Email", "Name", "Phone", "Position"],
            [
                {
                    Email: "charlie@ciilock.com",
                    Name: "Charlie Example",
                    Phone: "+61 3 1234 5678",
                    Position: "Engineer",
                },
            ],
            "email_enhance_example_input.xlsx",
            "Email Enhance Example"
        );

    return (
        <div className="min-h-screen bg-slate-50 p-4">
            <div className="mx-auto w-full space-y-6">
                <h1 className="mb-6 border-b border-slate-200 pb-4 text-center text-4xl font-extrabold text-slate-900">
                    Enhance &amp; Expand
                </h1>

                {/* Dropdowns & Download Buttons */}
                <div className="mb-6 flex items-start gap-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    {/* File Type */}
                    <div className="w-full max-w-[360px]">
                        <label htmlFor="fileType" className="mb-1 block text-sm font-medium text-slate-700">
                            Select File Type:
                        </label>
                        <select
                            id="fileType"
                            value={fileType}
                            onChange={handleFileTypeChange}
                            className="h-11 w-full rounded-md border border-slate-300 bg-white p-2 text-sm text-slate-700 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/20"
                        >
                            <option>Domain List</option>
                            {/* <option>LinkedIn URL</option> */}
                            <option>Email List</option>
                        </select>
                    </div>

                    {/* Operation */}
                    <div className="w-full max-w-[360px]">
                        <label htmlFor="operation" className="mb-1 block text-sm font-medium text-slate-700">
                            Select Operation:
                        </label>
                        <select
                            id="operation"
                            value={operation}
                            onChange={handleOperationChange}
                            className="h-11 w-full rounded-md border border-slate-300 bg-white p-2 text-sm text-slate-700 outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/20"
                        >
                            {fileType === "Domain List" && (
                                <>
                                    <option>Enhance Only</option>
                                    {/* <option>Expand & Enhance</option> */}
                                    <option>Enhance & Upload</option>
                                </>
                            )}
                            {fileType === "Email List" && (
                                <>
                                    <option>Enhance Only</option>
                                    {/* <option>Expand & Enhance</option> */}
                                    <option>Enhance & Upload</option>
                                </>
                            )}
                        </select>
                    </div>

                    {/* Example download */}
                    <div>
                        {fileType === "Domain List" && (
                            <div>
                                <label className="mb-1 block text-sm font-medium text-slate-700">Example:</label>
                                <button
                                    onClick={handleDownloadDomainExample}
                                    className="inline-flex items-center justify-center rounded-lg border border-slate-900 bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900/20"
                                >
                                    Download Example
                                </button>
                            </div>
                        )}

                        {fileType === "LinkedIn URL" && (
                            <div>
                                <label className="mb-1 block text-sm font-medium text-slate-700">Example:</label>
                                <button
                                    onClick={handleDownloadLinkedInExample}
                                    className="inline-flex items-center justify-center rounded-lg border border-slate-900 bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900/20"
                                >
                                    Download Example
                                </button>
                            </div>
                        )}

                        {fileType === "Email List" && (
                            <div>
                                <label className="mb-1 block text-sm font-medium text-slate-700">Example:</label>
                                <button
                                    onClick={operation === "Enhance Only" ? handleDownloadEmailEnhanceExample : handleDownloadEmailExample}
                                    className="inline-flex items-center justify-center rounded-lg border border-slate-900 bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900/20"
                                >
                                    Download Example
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Instructions */}
                <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm font-semibold text-amber-900">
                    <span className="font-bold">Note:</span> {instructions}
                </p>

                {/* Dropzone */}
                <div
                    {...getRootProps()}
                    className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center text-lg font-semibold shadow-sm transition-all duration-200 ease-in-out ${uploadedFile
                        ? "border-slate-900 bg-slate-50"
                        : "border-slate-300 bg-white hover:border-slate-900 hover:bg-slate-50"
                        }`}
                >
                    <input {...getInputProps()} />
                    <p className="text-slate-600">Drag &amp; drop a CSV or Excel file here, or click to select a file.</p>
                    {uploadedFile && <p className="mt-2 font-medium text-slate-900">File selected: {uploadedFile.name}</p>}
                </div>

                {/* Options + Submit */}
                {showOptions && (
                    <div className="mt-6 flex justify-end">
                        <div className="flex gap-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                            <label className="flex items-center text-sm font-medium text-slate-700">
                                <input type="checkbox" checked disabled className="mr-2 h-4 w-4 rounded border-slate-300" />
                                Add results to database
                            </label>

                            {!(operation === "Expand & Enhance" && fileType !== "LinkedIn URL") && (
                                <label
                                    className={`flex items-center text-sm font-medium text-slate-700 ${fileType === "LinkedIn URL" || operation === "Enhance & Upload"
                                        ? "cursor-not-allowed opacity-60"
                                        : "cursor-pointer"
                                        }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={emailResults}
                                        onChange={(e) => setEmailResults(e.target.checked)}
                                        disabled={fileType === "LinkedIn URL" || operation === "Enhance & Upload"}
                                        className="mr-2 h-4 w-4 rounded border-slate-300"
                                    />
                                    Email me results
                                </label>
                            )}

                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className={`rounded-md px-5 py-2.5 text-sm font-semibold shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-slate-900/20 ${isSubmitting
                                    ? "cursor-not-allowed bg-slate-300 text-white"
                                    : "bg-slate-900 text-white hover:bg-slate-800"
                                    }`}
                            >
                                {isSubmitting ? "Submitting..." : "Submit"}
                            </button>
                        </div>
                    </div>
                )}

                {/* Parsed Data Preview */}
                {parsedData.length > 0 && (
                    <div className="mt-8 overflow-x-auto rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                        <h2 className="mb-4 border-b border-slate-200 pb-2 text-xl font-bold text-slate-900">
                            Parsed Data Preview
                        </h2>
                        <table className="w-full table-auto border-collapse">
                            <thead>
                                <tr className="bg-slate-50">
                                    {Object.keys(parsedData[0]).map((key) => (
                                        <th
                                            key={key}
                                            className="border-b border-slate-200 px-4 py-3 text-left text-sm font-semibold uppercase tracking-wider text-slate-700"
                                        >
                                            {key}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {parsedData.map((row, idx) => (
                                    <tr key={idx} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                                        {Object.keys(parsedData[0]).map((col, i) => (
                                            <td key={i} className="whitespace-nowrap px-4 py-3 text-sm text-slate-700">
                                                {row[col]}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EnhanceAndExpand;
